import React, { FC, memo, useCallback } from 'react'

import { Header, MenuItemButton, SignalFeedback } from 'components'
import { Page, PageContent } from 'common'
import { Button } from 'common/styles/layouts'

import { Text } from '@interco/inter-ui/components/Text'

import { TPrivacyPolicyAcceptanceViewProps } from '../types'
import {
  CheckboxAndButtonContainer,
  PrivacyPoliciesContainer,
  CheckboxContainer,
  CheckboxContent,
  CustomCheckBoxContainer,
} from '../styles'
import { Content } from '../styles/privacyPolicyAcceptanceView.styles'
import { PrivacyPolicyAcceptanceContentSkeleton } from './PrivacyPolicyAcceptanceSkeleton'

function CustomCheckBox({
  checked,
  onChange,
  dataTestId,
}: {
  checked: boolean
  onChange: () => void
  dataTestId: string
}) {
  return (
    <CustomCheckBoxContainer
      type="checkbox"
      checked={checked}
      onChange={onChange}
      data-testid={dataTestId}
    />
  )
}

const EMPTY_HANDLER = () => undefined

interface IPrivacyPolicyMenuItemProps {
  versionId: string
  description: string
  url: string
  type: string
  menuItemIconRef: string
  menuItemButtonHandler: (url: string, title: string, type: string) => void
}

const PrivacyPolicyMenuItem = memo(
  ({
    versionId,
    description,
    url,
    menuItemIconRef,
    type,
    menuItemButtonHandler,
  }: IPrivacyPolicyMenuItemProps) => {
    const handleClick = useCallback(() => {
      menuItemButtonHandler(url, description, type)
    }, [menuItemButtonHandler, url, description, type])

    return (
      <MenuItemButton
        title={description}
        icon={menuItemIconRef}
        onClick={handleClick}
        dataTestId={`privacy-policy-menu-item-${versionId}`}
      />
    )
  },
)

interface IPrivacyPolicyCheckboxItemProps {
  versionId: string
  description: string
  checkBoxCommonText: string
  isChecked: boolean
  checkBoxHandler: (versionId: string) => void
}

const PrivacyPolicyCheckboxItem = memo(
  ({
    versionId,
    description,
    checkBoxCommonText,
    isChecked,
    checkBoxHandler,
  }: IPrivacyPolicyCheckboxItemProps) => {
    const handleClick = useCallback(() => {
      checkBoxHandler(versionId)
    }, [checkBoxHandler, versionId])

    return (
      <CheckboxContent onClick={handleClick}>
        <Text variant="body-3" color="typography" colorWeight={500}>
          {`${checkBoxCommonText} ${description}`}
        </Text>
        <CustomCheckBox
          checked={isChecked}
          onChange={EMPTY_HANDLER}
          dataTestId={`privacy-policy-checkbox-${versionId}`}
        />
      </CheckboxContent>
    )
  },
)

export const PrivacyPolicyAcceptanceView: FC<TPrivacyPolicyAcceptanceViewProps> = ({
  title,
  menuItemIconRef,
  checkBoxCommonText,
  checkBoxState,
  confirmButton,
  terms,
  isEmpty,
  emptyPage,
  isLoadingPending,
  checkBoxHandler,
  menuItemButtonHandler,
  onClickLeftIcon,
}) => (
  <Page>
    <Header title={title} onClickLeftIcon={onClickLeftIcon} leaveToApp />
    {isLoadingPending ? (
      <PrivacyPolicyAcceptanceContentSkeleton />
    ) : (
      <PageContent>
        {isEmpty && emptyPage ? (
          <div data-testid="privacy-policy-empty">
            <SignalFeedback
              icon={emptyPage.icon}
              title={emptyPage.title}
              details={emptyPage.subtitle}
              type={emptyPage.type}
            />
          </div>
        ) : (
          <Content data-testid="privacy-policy-content">
            <PrivacyPoliciesContainer>
              <section>
                {terms.map(({ versionId, description, url, type }) => (
                  <PrivacyPolicyMenuItem
                    key={versionId}
                    versionId={versionId}
                    description={description}
                    url={url}
                    type={type}
                    menuItemIconRef={menuItemIconRef}
                    menuItemButtonHandler={menuItemButtonHandler}
                  />
                ))}
              </section>
              <section />
            </PrivacyPoliciesContainer>
            <CheckboxAndButtonContainer>
              <CheckboxContainer data-testid="privacy-policy-checkboxes">
                {terms.map(({ versionId, description }) => {
                  const isChecked = checkBoxState.items[versionId] ?? false

                  return (
                    <PrivacyPolicyCheckboxItem
                      key={versionId}
                      versionId={versionId}
                      description={description}
                      checkBoxCommonText={checkBoxCommonText}
                      isChecked={isChecked}
                      checkBoxHandler={checkBoxHandler}
                    />
                  )
                })}
              </CheckboxContainer>
              <Button
                variant="primary"
                fullWidth
                onClick={confirmButton.handler}
                disabled={confirmButton.isDisabled}
                isLoading={confirmButton.isLoading}
                data-testid="privacy-policy-confirm-button"
              >
                {confirmButton.text}
              </Button>
            </CheckboxAndButtonContainer>
          </Content>
        )}
      </PageContent>
    )}
  </Page>
)
